import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PipelineProjectService from '@frontend/AppSettings/services/PipelineProjectService';

/**
 * A change order's lifecycle trail, on the card that moved it (gap review 2.16). The audit rows
 * always existed — every transition writes one in the same transaction — but their only reader
 * was the ADMIN_CATALOGS log; the people who AUTHOR the moves could not see their own trail.
 *
 * Collapsed by default and fetched only on expand: N cards on a project must not mean N history
 * requests nobody asked for. The route carries the CO list's own SEE_FINANCIALS gate, so anyone
 * who can see this card may see its trail.
 */
interface CoHistoryProps {
  matterid: string;
  coId: string;
}

const CoHistory = ({ matterid, coId }: CoHistoryProps): JSX.Element => {
  const [expanded, setExpanded] = useState(false);

  const history = useQuery({
    queryKey: ['ChangeOrderHistory', matterid, coId],
    queryFn: () => PipelineProjectService.getChangeOrderHistory(matterid, coId),
    enabled: expanded,
  });

  return (
    <div className="pipeline-co-history">
      <button
        type="button"
        className="pipeline-secondary-btn"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        {expanded ? 'Hide history' : 'History'}
      </button>
      {expanded && history.isLoading && <p className="pipeline-muted">Loading history…</p>}
      {expanded && history.isError && (
        <p className="pipeline-muted">Could not load the history.</p>
      )}
      {expanded && history.data && (
        <ul className="pipeline-co-trail">
          {history.data.map((entry, i) => (
            <li key={i}>
              {/* Keyed on the ACTION, not on a null from_status: replace_change_order writes
                  edit rows with no status pair, and reading those as creations rendered a
                  fabricated-looking "Opened at ?" per edit on a surface that calls itself
                  evidence (review). */}
              {entry.action === 'create'
                ? `Opened at ${entry.to_status ?? '?'}`
                : entry.from_status || entry.to_status
                  ? `${entry.from_status ?? '?'} → ${entry.to_status ?? '?'}`
                  : 'Details updated'}
              {' · '}
              {entry.performed_at.slice(0, 16)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CoHistory;
